/**
 * PremiumNotice
 *
 * The explanation a user gets when they try to pick something that is behind the paywall.
 *
 * ## Why this exists
 *
 * Locked rows in the coverage and formation dropdowns used to do *nothing at all* when clicked —
 * `handleSelect` returned early and there was no modal, no message, and no route to the pricing
 * page. A user who clicked "Cover 4 Match" twice and got silence has no way to tell a paywall from
 * a bug, and we lose the clearest possible buying signal: someone naming the exact feature they
 * want.
 *
 * So the lock now answers. The notice appears inside the open dropdown, directly under the row that
 * was clicked, names the thing they asked for, and offers a way through.
 *
 * ## Why inline rather than a modal or a redirect
 *
 * A modal or an immediate jump to /pricing throws away the play they were building. Answering in
 * place keeps them in the simulator and makes the upgrade a choice instead of an interruption.
 *
 * The link is a router `<Link>`, not a bare `<a href>`. The existing upgrade links across the app
 * are raw anchors, which force a full page reload and drop the user's simulation state.
 */

import { Link } from 'react-router-dom';
import './PremiumNotice.css';

export interface PremiumNoticeProps {
  /** What the user tried to select, e.g. "Cover 4 Match". Named so the message is specific. */
  featureName: string;
  /** What they get by upgrading, phrased for this particular lock. */
  detail: string;
  /** Called when the user follows the link, so the caller can close its dropdown. */
  onNavigate?: () => void;
}

function PremiumNotice({ featureName, detail, onNavigate }: PremiumNoticeProps) {
  return (
    // `role="status"` so the message is announced when it appears — a keyboard user activating a
    // locked row gets told why nothing happened, rather than being left guessing.
    <div className="premium-notice" role="status">
      <p className="premium-notice-text">
        <strong>{featureName}</strong> is a Premium coverage. {detail}
      </p>
      <Link to="/pricing" className="premium-notice-link" onClick={onNavigate}>
        See Premium plans
      </Link>
    </div>
  );
}

export default PremiumNotice;
